import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt, FaFolder, FaFolderOpen } from 'react-icons/fa';
import { getDocumentFields, Field, getDocumentTypes, http } from '../api';
import { DocumentGrid } from './DocumentGrid';
import { DocumentModals } from './DocumentModals';
import { useDocumentActions } from './useDocumentActions';
import './BrowsePage.css';

interface FolderNode { id: number; name: string; children?: FolderNode[]; isLoading?: boolean; isExpanded?: boolean; loaded?: boolean; hasChildren?: boolean; }
interface DocumentResult { id?: number; uri?: string; extension?: string; documentTypeId?: any; documentValues?: Record<string, any>; folder?: any; }
interface DocumentType { id: any; documentTypeName: string; documentTypeExtension?: string; }

function getDocumentIcon(extension: string) {
  switch (extension?.toLowerCase()) {
    case 'pdf': return <FaFilePdf size={20} color="#E53935" title="PDF" />;
    case 'doc': case 'docx': return <FaFileWord size={20} color="#1976D2" title="DOC" />;
    case 'xls': case 'xlsx': return <FaFileExcel size={20} color="#388E3C" title="XLS" />;
    default: return <FaFileAlt size={20} color="#757575" title="DOC" />;
  }
}

// default page size (can be changed by user in grid)
const DEFAULT_PAGE_SIZE = 100;
const PANEL_WIDTH_KEY = 'browsePanelWidth';

export default function BrowsePage({ drawerId }: { drawerId: number | null }) {
  const [rootFolders, setRootFolders] = useState<FolderNode[]>([]);
  const [rootSkip, setRootSkip] = useState(0);
  const [hasMoreRoots, setHasMoreRoots] = useState(false);
  const [loadingRoots, setLoadingRoots] = useState(false);
  const [folderFilter, setFolderFilter] = useState('');

  const [selectedFolder, setSelectedFolder] = useState<FolderNode | null>(null);
  const [childFolders, setChildFolders] = useState<FolderNode[]>([]);
  const [childrenSkip, setChildrenSkip] = useState(0);
  const [childrenHasMore, setChildrenHasMore] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);

  const [documents, setDocuments] = useState<DocumentResult[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [resultColumns, setResultColumns] = useState<string[]>([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [documentFields, setDocumentFields] = useState<Field[]>([]);

  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_KEY);
    const initial = saved ? Number(saved) : 500;
    return Math.max(500, Math.min(isNaN(initial) ? 700 : initial, 500));
  });
  const resizing = useRef(false);

  // page size persisted per drawer (declare early so fetch functions can use it)
  const pageSizeKey = `browse_page_size_${drawerId ?? 'none'}`;
  const [pageSize, setPageSize] = useState<number>(() => {
    try { const raw = localStorage.getItem(pageSizeKey); const v = raw ? Number(raw) : DEFAULT_PAGE_SIZE; return Number.isFinite(v) && v > 0 ? v : DEFAULT_PAGE_SIZE; } catch { return DEFAULT_PAGE_SIZE; }
  });

  useEffect(()=>{ document.documentElement.style.setProperty('--browse-panel-width', panelWidth + 'px'); },[panelWidth]);

  const documentOnlyFields = documentFields.filter(f => !f.folderfield);

  // Document Actions Hook
  const documentActions = useDocumentActions({
    fields: documentFields,
    onDocumentUpdate: (docId, updatedDoc) => {
      setDocuments(prev => prev.map(d => 
        (d as any).id === docId ? { ...d, ...updatedDoc } : d
      ));
    }
  });

  useEffect(()=> { if(!drawerId){ setDocumentFields([]); return; } getDocumentFields(drawerId).then(setDocumentFields).catch(()=>setDocumentFields([])); },[drawerId]);

  // Root folders fetching with filter + paging
  const fetchRootFolders = async (append: boolean, skipOverride?: number) => {
    if (!drawerId) return;
    setLoadingRoots(true);
    try {
      const skip = skipOverride ?? (append ? rootSkip : 0);
      const url = `/api/folder/bydrawer?drawerid=${drawerId}`
        + (folderFilter ? `&filter=${encodeURIComponent(folderFilter)}` : '')
        + `&skip=${skip}&take=${pageSize}`;
      const data: any[] = await http<any[]>(url);
      const mapped = data.map(d=>({ id:d.id??d.Id, name:d.name??d.Name, hasChildren: d.hasChildren ?? d.HasChildren } as FolderNode));
      setHasMoreRoots(mapped.length === pageSize);
      if (append) {
        setRootFolders(prev => {
          const existingIds = new Set(prev.map(p=>p.id));
          const toAdd = mapped.filter(m => !existingIds.has(m.id));
          return [...prev, ...toAdd];
        });
        setRootSkip(skip + pageSize);
      } else {
        setRootFolders(mapped);
        setRootSkip(pageSize);
      }
    } catch (e) {
      setHasMoreRoots(false);
      setError('Failed loading folders');
    } finally {
      setLoadingRoots(false);
    }
  };

  // Children folders fetching with paging (grid list)
  const fetchChildren = async (folderId: number, append: boolean, skipOverride?: number) => {
    if (!drawerId) return;
    setLoadingChildren(true);
    try {
      const skip = skipOverride ?? (append ? childrenSkip : 0);
      const url = `/api/folder/byparent?drawerid=${drawerId}&folderid=${folderId}&skip=${skip}&take=${pageSize}`;
      const data: any[] = await http<any[]>(url);
      const mapped = data.map(d=>({ id:d.id??d.Id, name:d.name??d.Name, hasChildren: d.hasChildren ?? d.HasChildren } as FolderNode));
      setChildrenHasMore(mapped.length === pageSize);
      if (append) {
        setChildFolders(prev => {
          const existingIds = new Set(prev.map(p=>p.id));
          const toAdd = mapped.filter(m => !existingIds.has(m.id));
          return [...prev, ...toAdd];
        });
        setChildrenSkip(skip + pageSize);
      } else {
        setChildFolders(mapped);
        setChildrenSkip(pageSize);
      }
    } catch (e) {
      setChildrenHasMore(false);
    } finally {
      setLoadingChildren(false);
    }
  };

  // helper: find a node by id in the current tree (any depth)
  function findNodeById(nodes: FolderNode[], id: number): FolderNode | null {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.children && n.children.length) {
        const f = findNodeById(n.children, id);
        if (f) return f;
      }
    }
    return null;
  }

  // Debounce filter input (placeholder for actual debounce if needed)
  const debouncedFilter = useMemo(() => folderFilter, [folderFilter]);

  useEffect(()=>{
    // Reset on drawer or filter change
    if (!drawerId) { 
      setRootFolders([]); setSelectedFolder(null); setHasMoreRoots(false); setRootSkip(0); 
      setChildFolders([]); setChildrenSkip(0); setChildrenHasMore(false);
      return; 
    }
    fetchRootFolders(false, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerId, debouncedFilter]);

  useEffect(()=>{ if(!drawerId) return; getDocumentTypes().then(setDocumentTypes).catch(()=>setDocumentTypes([])); },[drawerId]);

  function toggleFolder(node: FolderNode) {
    if (!node.hasChildren) return;
    if (node.isExpanded) { node.isExpanded = false; setRootFolders([...rootFolders]); return; }
    node.isExpanded = true;
    if (!node.loaded) {
      node.isLoading = true;
      http<any>(`/api/folder/byparent?drawerid=${drawerId}&folderid=${node.id}`)
        .then(children => {
          node.children = (children as any[]).map(c=>({ id:c.id??c.Id, name:c.name??c.Name, hasChildren: c.hasChildren ?? c.HasChildren }));
          node.loaded = true; node.isLoading=false; setRootFolders([...rootFolders]);
        })
        .catch(()=> { node.children = []; node.loaded = true; node.isLoading=false; setRootFolders([...rootFolders]); });
    } else setRootFolders([...rootFolders]);
  }

  // Select and also expand/open the folder in the tree, loading children if needed
  function selectFolder(node: FolderNode) { 
    setSelectedFolder(node);
    if (node.hasChildren) {
      if (!node.isExpanded) node.isExpanded = true;
      if (!node.loaded) {
        node.isLoading = true;
        http<any>(`/api/folder/byparent?drawerid=${drawerId}&folderid=${node.id}`)
          .then(children => {
            node.children = (children as any[]).map(c=>({ id:c.id??c.Id, name:c.name??c.Name, hasChildren: c.hasChildren ?? c.HasChildren }));
            node.loaded = true; node.isLoading = false; setRootFolders([...rootFolders]);
          })
          .catch(() => { node.children = []; node.loaded = true; node.isLoading = false; setRootFolders([...rootFolders]); });
      } else {
        setRootFolders([...rootFolders]);
      }
    } else {
      setRootFolders([...rootFolders]);
    }
  }

  // When selected folder changes, refresh its child folders (for grid) and documents
  useEffect(() => {
    setChildFolders([]); setChildrenSkip(0); setChildrenHasMore(false);
    if (drawerId && selectedFolder) {
      fetchChildren(selectedFolder.id, false, 0);
    }
    // Documents will be loaded in the next effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder?.id, drawerId]);

  useEffect(()=>{
    async function loadDocs() {
      if (!drawerId || !selectedFolder) { setDocuments([]); setResultColumns([]); return; }
      setLoadingDocs(true); setError(null);
      try {
        const searchRequest = {
          Filter: `drawer eq ${drawerId} and folder eq ${selectedFolder.id}`,
          Take: pageSize,
          OrderBy: undefined,
          ContinuationToken: undefined
        };
        
        const data = await http<any>('/api/document/search', { method: 'POST', json: searchRequest });
        const docs: DocumentResult[] = data.Values ?? data.values ?? [];
        setDocuments(docs);
        const discovered = new Set<string>();
        docs.forEach(d=> { if (d.documentValues) Object.keys(d.documentValues).forEach(k=> discovered.add(k)); });
        const ordered = documentFields.filter(f => discovered.has(f.name)).map(f=> f.name);
        const remaining = Array.from(discovered).filter(k => ordered.indexOf(k)===-1);
        const all = [...ordered, ...remaining];
        setResultColumns(all);
      } catch(e:any){ setError(e.message); setDocuments([]); setResultColumns([]); }
      finally { setLoadingDocs(false); }
    }
    loadDocs();
  },[drawerId, selectedFolder, documentFields]);

  // Handler when DocumentGrid notifies a page size change
  function handlePageSizeChange(size: number) {
    try { localStorage.setItem(pageSizeKey, String(size)); } catch {}
    setPageSize(size);
    // effect watching pageSize will reload lists
  }

  function handleResultColumnToggle(col: string) { setResultColumns(prev => prev.indexOf(col)!==-1 ? prev.filter(c=>c!==col) : [...prev, col]); }

  async function handleFolderOpenFromGrid(folderId: number) {
    // Try to mimic clicking in the tree: expand current selected folder in tree (as parent), load its children if needed, then select the child
    const currentParentId = selectedFolder?.id;
    if (!currentParentId) { setSelectedFolder({ id: folderId, name: 'Folder', hasChildren: true }); return; }

    const treeParent = findNodeById(rootFolders, currentParentId);
    if (!treeParent) { setSelectedFolder({ id: folderId, name: 'Folder', hasChildren: true }); return; }

    treeParent.isExpanded = true;
    if (!treeParent.loaded) {
      treeParent.isLoading = true;
      try {
        const children: any[] = await http<any>(`/api/folder/byparent?drawerid=${drawerId}&folderid=${treeParent.id}`);
        treeParent.children = (children as any[]).map(c=>({ id:c.id??c.Id, name:c.name??c.Name, hasChildren: c.hasChildren ?? c.HasChildren }));
        treeParent.loaded = true;
      } catch {
        treeParent.children = [];
        treeParent.loaded = true;
      } finally {
        treeParent.isLoading = false;
        setRootFolders([...rootFolders]);
      }
    } else {
      setRootFolders([...rootFolders]);
    }

    // After ensuring children
    const child = treeParent.children?.find(c => c.id === folderId);
    if (child) setSelectedFolder({ ...child }); else setSelectedFolder({ id: folderId, name: 'Folder', hasChildren: true });
  }

  // Resizer handlers
  useEffect(() => { localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth)); }, [panelWidth]);
  function handleMouseDown(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    resizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }
  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!resizing.current) return;
      setPanelWidth(Math.max(220, Math.min(e.clientX, 700)));
    }
    function onUp() {
      if (!resizing.current) return;
      resizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  function renderTree(nodes: FolderNode[]) {
    return (
      <ul className="folder-tree">
        {nodes.map(n=> (
          <li key={n.id}>
            <div className={`folder-row ${selectedFolder?.id===n.id? 'active':''}`}>              
              {n.hasChildren ? (
                <button type="button" className="folder-toggle" aria-label={n.isExpanded? 'Collapse folder':'Expand folder'} onClick={()=> toggleFolder(n)}>
                  {n.isExpanded ? '-' : '+'}
                </button>
              ) : (<span style={{ width:18 }} />)}
              <span className={`folder-icon ${n.isExpanded? 'open':''}`}>{n.isExpanded? <FaFolderOpen size={16}/> : <FaFolder size={16}/>}</span>
              <span onClick={()=> selectFolder(n)} style={{ flex:1 }}>{n.name}</span>
            </div>
            {n.isExpanded && n.hasChildren && (
              n.isLoading ? <div className="folder-empty" style={{ marginLeft:32 }}>Loading...</div> : (
                n.children && n.children.length>0 ? <div className="folder-children">{renderTree(n.children)}</div> : <div className="folder-empty">No subfolders</div>
              )
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="browse-container">
      <div className="browse-panel">
        <h3 className="browse-title">Folders</h3>
        <div className="folder-filter">
          <input
            type="text"
            value={folderFilter}
            onChange={e => setFolderFilter(e.target.value)}
            placeholder="Filter folders..."
            className="input-default"
          />
        </div>
        {drawerId ? (
          rootFolders.length===0 && !loadingRoots ? <div className="folder-empty">No folders.</div> : (
            <>
              {renderTree(rootFolders)}
              <div style={{ padding:'4px 0' }}>
                {hasMoreRoots && (
                  <button type="button" disabled={loadingRoots} onClick={()=> fetchRootFolders(true)} className="btn-load">
                    {loadingRoots ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </div>
            </>
          )
        ) : <div>Select a drawer.</div>}
      </div>
      <div className="resizer" onMouseDown={handleMouseDown} />
      <div className="results-area">
        {error && <div className="error-text">{error}</div>}
        <DocumentGrid
          documents={documents}
          fields={documentFields}
          documentTypes={documentTypes}
          resultColumns={resultColumns}
          onColumnToggle={handleResultColumnToggle}
          showColumnSelector={showColumnSelector}
          onToggleColumnSelector={() => setShowColumnSelector(s => !s)}
          onEdit={documentActions.openEdit}
          onAudit={documentActions.openAudit}
          onMetadata={documentActions.openMetadata}
          emptyMessage={selectedFolder ? 'No documents found.' : 'Select a folder to view documents.'}
          // New: folder rows above documents
          folders={childFolders.map(c => ({ id: c.id, name: c.name }))}
          onFolderOpen={handleFolderOpenFromGrid}
          loadingFolders={loadingChildren}
          foldersHasMore={childrenHasMore}
          onLoadMoreFolders={() => selectedFolder && fetchChildren(selectedFolder.id, true)}
          drawerId={drawerId}
          storagePrefix={'browse'}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <DocumentModals
        editDoc={documentActions.editDoc}
        editValues={documentActions.editValues}
        savingEdit={documentActions.savingEdit}
        editError={documentActions.editError}
        documentFields={documentOnlyFields}
        onEditValuesChange={documentActions.setEditValues}
        onEditCancel={documentActions.cancelEdit}
        onEditSave={documentActions.saveEdit}
        auditDoc={documentActions.auditDoc}
        audits={documentActions.audits}
        loadingAudits={documentActions.loadingAudits}
        auditError={documentActions.auditError}
        onAuditClose={documentActions.closeAudit}
        metadataOpen={documentActions.metadataOpen}
        metadataRows={documentActions.metadataRows}
        onMetadataClose={documentActions.closeMetadata}
      />
    </div>
  );
}
